<template>
  <div>
    <el-form
      ref="formObj"
      :model="form"
      :rules="rules"
      :label-position="isDesktop ? 'left' : 'top'"
      :class="{ desktop: isDesktop }"
      label-width="200px"
      @submit.prevent
    >
      <el-alert
        v-if="!disabled"
        type="warning"
        effect="dark"
        show-icon
        :closable="false"
      >
        <span v-html="alertText"></span>
      </el-alert>
      <el-alert
        v-if="flatDeal.traffic_delivery_warning"
        type="warning"
        effect="light"
        show-icon
        :closable="false"
      >
        {{ flatDeal.traffic_delivery_warning }}
      </el-alert>
      <el-form-item
        label="Name"
        prop="deal_name"
        :error="errors.deal_name || errors.name"
      >
        <el-input v-model="form.deal_name" :disabled="disabled" type="text" />
      </el-form-item>
      <el-form-item
        v-if="
          !flatDeal.daily_limit && flatDeal.deal_type === FlatDealType.CpmRate
        "
        label="Daily limit"
        prop="daily_limit"
        :error="errors.daily_limit"
      >
        <el-input v-model="form.daily_limit" :disabled="disabled" type="number">
          <template #append>
            Expected Deal Price: {{ expectedDealPrice }}$
          </template>
        </el-input>
      </el-form-item>
      <template v-if="videoType">
        <VideoSelector v-model="form.video_source" :disabled="disabled" />
        <el-form-item
          v-if="VideoSource.File === form.video_source"
          :error="errors.video"
          prop="video"
          label="Upload video file"
          size="small"
        >
          <FileUpload
            :thumb="flatDeal.video_preview"
            :multiple="false"
            :disabled="disabled"
            :tooltip="VIDEO_UPLOAD_TOOLTIP"
            allowed-formats=".mp4"
            @handleChange="
              (file, ref, list) => handleFileChange(file, ref, list, 'video')
            "
          />
        </el-form-item>
        <el-form-item
          v-if="VideoSource.Url === form.video_source"
          :error="errors.remote_url"
          prop="remote_url"
          label="Add video URL"
        >
          <RemoteVideoInput v-model="form.remote_url" :disabled="disabled" />
        </el-form-item>
        <el-form-item
          v-if="VideoSource.Tag === form.video_source"
          :error="errors.vast_tag"
          prop="vast_tag"
          label="Add VAST tag"
        >
          <VastTagInput v-model="form.vast_tag" :disabled="disabled" />
        </el-form-item>
        <el-form-item
          v-if="VideoSource.Tag !== form.video_source"
          :error="errors.skip_offset"
          prop="skip_offset"
          label="Skip offset"
        >
          <SkipOffsetInput v-model="form.skip_offset" :disabled="disabled" />
        </el-form-item>
      </template>
      <el-form-item
        v-if="linkActive"
        :error="errors.link_url"
        :label="linkLabel"
        prop="link_url"
      >
        <LinkUrlInput v-model="form.link_url" :disabled="disabled" />
      </el-form-item>
      <el-form-item
        v-if="extendedSettingsAllowed"
        label="Impression tracking URL"
      >
        <el-input
          v-model="form.impression_url"
          :disabled="disabled"
          type="text"
        />
      </el-form-item>
      <el-form-item v-if="isStaff" label="Code">
        <CodeInput v-model="form.code" :disabled="disabled" />
      </el-form-item>
      <template v-if="AdType.Banner === flatDeal.type_id">
        <el-form-item
          :error="errors.image"
          label="Image"
          prop="image"
          size="small"
        >
          <FileUpload
            :thumb="flatDeal.image_preview"
            :multiple="false"
            :disabled="disabled"
            :tooltip="IMAGE_UPLOAD_TOOLTIP"
            allowed-formats=".jpg, .jpeg, .png, .gif, .mp4"
            @handleChange="
              (file, ref, list) => handleFileChange(file, ref, list, 'image')
            "
          />
        </el-form-item>
      </template>
      <template v-if="AdType.InpagePush === flatDeal.type_id">
        <el-form-item
          :error="errors.icon"
          label="Icon"
          size="small"
          prop="icon"
        >
          <FileUpload
            :thumb="flatDeal.icon_preview"
            :multiple="false"
            :disabled="disabled"
            :tooltip="ICON_UPLOAD_TOOLTIP"
            allowed-formats=".jpg, .jpeg, .png, .gif, .mp4"
            @handleChange="
              (file, ref, list) => handleFileChange(file, ref, list, 'icon')
            "
          />
        </el-form-item>
        <el-form-item
          prop="title"
          :error="errors.title"
          :disabled="disabled"
          label="Title"
        >
          <el-input v-model="form.title" type="text" />
        </el-form-item>
        <el-form-item
          prop="description"
          :error="errors.description"
          :disabled="disabled"
          label="Description"
        >
          <el-input v-model="form.description" type="textarea" />
        </el-form-item>
      </template>
    </el-form>
    <slot />
    <el-form v-if="!disabled" label-width="200px">
      <el-form-item>
        <el-tooltip
          effect="dark"
          :disabled="!notAllowedDeal"
          placement="top-start"
          :content="moneyErrorTitle"
        >
          <div>
            <el-button
              type="success"
              plain
              :disabled="notAllowedDeal || loading"
              @click="onSubmit"
            >
              Save and send to review
            </el-button>
            <router-link to="/direct_deals" class="el-button is-plain">
              <span>Cancel</span>
            </router-link>
          </div>
        </el-tooltip>
      </el-form-item>
    </el-form>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import {
  AdType,
  VideoSource,
  FlatDealValidationErrorsKey,
  FlatDealType
} from '@/api/types'
import LinkUrlInput from '@/apps/traforama/components/LinkUrlInput.vue'
import useValidation from '@/hooks/useValidation'

import FileUpload from '@/components/FileUpload.vue'
import VideoSelector from '@/apps/traforama/views/creatives/components/VideoSelector.vue'
import VastTagInput from '@/apps/traforama/components/VastTagInput.vue'
import RemoteVideoInput from '@/apps/traforama/components/RemoteVideoInput.vue'
import SkipOffsetInput from '@/apps/traforama/components/SkipOffsetInput.vue'
import CodeInput from '@/apps/traforama/components/CodeInput.vue'
import useResize from '@/hooks/useResize'
import {
  useFileUpload,
  IMAGE_UPLOAD_TOOLTIP,
  ICON_UPLOAD_TOOLTIP,
  VIDEO_UPLOAD_TOOLTIP,
  UploadTypes
} from '@/hooks/useFileUpload'

import { useUserStore } from '@/store/modules/user'

export default defineComponent({
  name: 'FlatDealForm',
  components: {
    LinkUrlInput,
    FileUpload,
    VideoSelector,
    VastTagInput,
    RemoteVideoInput,
    SkipOffsetInput,
    CodeInput
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    flatDeal: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const userStore = useUserStore()

    const { isStaff } = userStore
    const alertText =
      '<p>By clicking the Buy button, you agree to an authorization hold to be put on your balance for the funds equal to the whole sum of the deal. After the deal is approved and starts running, your balance will be charged. The purchase of a flat deal can not be canceled. <a class="el-link el-link--primary is-underline" href="https://support.traforama.com/en/articles/flat-deal-terms" target="_blank">More details.</a></p>'
    const moneyErrorTitle = ref('Not enough money to start this deal')
    const { validationErrors } = useValidation(FlatDealValidationErrorsKey)
    const errors = validationErrors()
    const { isDesktop } = useResize()
    const form = ref({ ...props.modelValue })
    const formObj = ref()

    const videoType = computed(() => {
      return [AdType.Slider, AdType.Overlay, AdType.OutStream].includes(
        props.flatDeal.type_id
      )
    })

    const rules = {
      deal_name: {
        required: true,
        message: 'Field is required',
        trigger: 'change'
      },
      code: { required: true, message: 'Field is required', trigger: 'change' },
      daily_limit: {
        required: true,
        min: 1,
        message: 'Limit must be greater than 0',
        trigger: 'change'
      },
      video: {
        required: !isStaff,
        message: 'Please upload a video',
        trigger: 'change'
      },
      icon: {
        required: !isStaff,
        message: 'Please upload an icon',
        trigger: 'change'
      },
      image: {
        required: !isStaff,
        message: 'Please upload an image',
        trigger: 'change'
      },
      link_url: { required: !isStaff, message: 'Wrong url', trigger: 'change' },
      skip_offset: {
        required: true,
        message: 'Skip offset should be 0.1 to 10',
        trigger: 'change'
      },
      vast_tag: {
        required: true,
        message: 'Field is required',
        trigger: 'change'
      },
      remote_url: {
        required: true,
        message: 'Field is required',
        trigger: 'change'
      },
      title: {
        required: true,
        message: 'Field is required',
        trigger: 'change'
      },
      description: {
        required: true,
        message: 'Field is required',
        trigger: 'change'
      }
    }

    const extendedSettingsAllowed = computed(() => {
      if (!userStore.extendedSettingsAllowed) {
        return false
      }
      return ![AdType.Popunder, AdType.Interstitial].includes(
        props.flatDeal.type_id
      )
    })

    const linkActive = computed(() => {
      return (
        (videoType.value && VideoSource.Tag !== form.value.video_source) ||
        !videoType.value
      )
    })

    const linkLabel = computed(() => {
      if (
        [
          AdType.Banner,
          AdType.Overlay,
          AdType.Slider,
          AdType.OutStream
        ].includes(props.flatDeal.type_id)
      ) {
        return 'Click URL'
      }
      return 'Link URL'
    })

    const expectedDealPrice = computed(() => {
      if (props.flatDeal.total_price) return props.flatDeal.total_price
      if (props.flatDeal.daily_limit) return 0
      if (!form.value.daily_limit) return 0

      return (
        (props.flatDeal.total_days *
          props.flatDeal.flat_rate *
          form.value.daily_limit) /
        1000
      ).toFixed(2)
    })

    const notAllowedDeal = computed(() => {
      return userStore.balance / 100 < expectedDealPrice.value && !isStaff
    })

    const handleFileChange = (
      file: any,
      ref: any,
      list: any[],
      type: UploadTypes
    ) => {
      if (list.length > 1) list.shift()

      const isValid = useFileUpload(file, ref, type, errors)
      if (isValid) {
        errors.value = {}
        form.value[type] = file.raw
      }
    }
    const onSubmit = () => {
      formObj.value.validate(async (valid: boolean) => {
        if (valid) {
          if (notAllowedDeal.value) {
            errors.value = { deal_name: moneyErrorTitle.value }
            return false
          }

          switch (form.value.video_source) {
            case VideoSource.File: {
              form.value.vast_tag = ''
              form.value.remote_url = ''
              break
            }
            case VideoSource.Url: {
              form.value.video = ''
              form.value.vast_tag = ''
              break
            }
            case VideoSource.Tag: {
              form.value.video = ''
              form.value.remote_url = ''
              break
            }
          }
          emit('submit', form.value)
        } else {
          return false
        }
      })
    }

    return {
      videoType,
      AdType,
      form,
      linkLabel,
      onSubmit,
      extendedSettingsAllowed,
      linkActive,
      handleFileChange,
      expectedDealPrice,
      FlatDealType,
      isStaff,
      VideoSource,
      moneyErrorTitle,
      formObj,
      notAllowedDeal,
      alertText,
      isDesktop,
      rules,
      errors,
      IMAGE_UPLOAD_TOOLTIP,
      ICON_UPLOAD_TOOLTIP,
      VIDEO_UPLOAD_TOOLTIP
    }
  }
})
</script>

<style lang="scss" scoped>
.logo-container {
  height: 70%;
}
.logo {
  max-width: -webkit-fill-available;
  max-height: 100%;
}
.flex-container {
  display: flex;
  justify-content: space-between;
  &.center {
    justify-content: center;
  }
  &.around {
    justify-content: space-around;
  }
}
.footer {
  padding: 10px 5px 0 5px;
  border-top: 1px solid #e6ebf5;
}
.body {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.el-form {
  margin: 0 20px;
}
.el-card {
  padding-bottom: 5px;
  margin-top: 15px;
  &:hover {
    cursor: pointer;
  }
  &.disabled {
    opacity: 0.4;
  }
}
:deep(.el-alert) {
  margin-bottom: 10px;
}
</style>
